import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { CorePermission, PartnerTier } from "@iloq/shared";
import React from "react";
import { FormattedMessage, IntlProvider } from "react-intl";
import { ProfileIcon } from "../CustomIcons";
import FeatureGuard from "../FeatureGuard";
import ReactAppContainer from "../ReactAppContainer";
import { VueContextType } from "../vueContext";

const { Fragment } = React;

export default function MenuRootComponent(props: VueContextType): React.ReactElement {
  return (
    <ReactAppContainer vueProps={props}>
      <IntlProvider
        locale={props.locale}
        messages={props.messages[props.locale]}
        textComponent={Fragment}
      >
        <ProfileMenu {...props} />
      </IntlProvider>
    </ReactAppContainer>
  );
}

function ProfileMenuLink({
  children,
  ...props
}: React.PropsWithChildren<{
  [key: string]: any;
}>): React.ReactElement {
  return (
    <Link
      pt="lg"
      pb="lg"
      flex={1}
      width="100%"
      _hover={{
        textDecoration: "none",
      }}
      {...props}
    >
      {children}
    </Link>
  );
}

function ProfileMenu(props: VueContextType): React.ReactElement {
  const { $router } = props;
  const {
    partnerInfo: { partnerInfo },
    user,
  } = props.$store.state;

  const isPlatinum = partnerInfo.marketingTier === PartnerTier.PLATINUM;

  const textColor = isPlatinum ? "brand.purple" : "white";

  const isAuthorizedForPartnerProgram = user.permissions.includes(
    CorePermission.partnerProgram || CorePermission.partnerProgramConsumer,
  );
  const isAuthorizedForPartnerInfoManagement = user.permissions.includes(
    CorePermission.partnerInfoManagement,
  );
  const isAuthorizedForUserManagement = user.permissions.includes(
    CorePermission.viewUserManagement,
  );

  return (
    <Menu isLazy autoSelect={false}>
      {({ isOpen }) => (
        <>
          <MenuButton height="52px" data-cy="menu-button" mx="0.5rem">
            <Flex alignItems="center">
              <ProfileIcon boxSize={9} color={textColor} />
              <Spacer width={2} />
              <Text ml="0.25rem" fontSize="sm" fontWeight="light" display={["none", "inherit"]}>
                {user.firstName} {user.lastName}
              </Text>
            </Flex>
          </MenuButton>

          <Drawer isOpen={isOpen} placement="right" onClose={() => {}}>
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader background="brand.beige" p={["lg", "2xl"]}>
                <Text>
                  {user.firstName} {user.lastName}
                </Text>
                <Text fontSize="smaller" mt="lg">
                  {partnerInfo.partnerName}
                </Text>
                <Text mt="xs" fontWeight="light" fontSize="smaller" color="gray.700">
                  <FormattedMessage id="partnerProgram.authorizedPartner" />
                </Text>
              </DrawerHeader>

              <DrawerBody
                pt={["xs", "xs"]}
                pr={["lg", "2xl"]}
                pb={["lg", "2xl"]}
                pl={["lg", "2xl"]}
              >
                <FeatureGuard feature="partnerProgram">
                  {isAuthorizedForPartnerProgram && (
                    <MenuItem>
                      <ProfileMenuLink
                        href="/partner_program"
                        onClick={(e: React.SyntheticEvent) => {
                          e.preventDefault();
                          $router.history?.push("/partner_program");
                        }}
                        data-cy="partner-program-link"
                      >
                        <FormattedMessage id="profileMenu.partnerProgramLink" />
                      </ProfileMenuLink>
                    </MenuItem>
                  )}
                </FeatureGuard>
                <FeatureGuard feature="partnerInfoManagement">
                  {isAuthorizedForPartnerInfoManagement && (
                    <MenuItem>
                      <ProfileMenuLink
                        href="/partner_info_management"
                        onClick={(e: React.SyntheticEvent) => {
                          e.preventDefault();
                          $router.history?.push("/partner_info_management");
                        }}
                        data-cy="partner-info-management-link"
                      >
                        <FormattedMessage id="profileMenu.partnerInfoManagementLink" />
                      </ProfileMenuLink>
                    </MenuItem>
                  )}
                </FeatureGuard>
                <FeatureGuard feature="userManagement">
                  {isAuthorizedForUserManagement && (
                    <MenuItem>
                      <ProfileMenuLink
                        href="/partner_user_management"
                        onClick={(e: React.SyntheticEvent) => {
                          e.preventDefault();
                          $router.history?.push("/partner_user_management");
                        }}
                        data-cy="partner-user-management-link"
                      >
                        <FormattedMessage id="profileMenu.userManagementLink" />
                      </ProfileMenuLink>
                    </MenuItem>
                  )}
                </FeatureGuard>
                <MenuItem>
                  <ProfileMenuLink
                    href="/logout"
                    onClick={(e: React.SyntheticEvent) => {
                      e.preventDefault();
                      $router.history?.push("/logout");
                    }}
                  >
                    <FormattedMessage id="profileMenu.signOut" />
                  </ProfileMenuLink>
                </MenuItem>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </Menu>
  );
}
